import z, { ZodTypeAny } from 'zod'
import { VectorSquare } from 'src/server/utils/geo-json-features'
import { isWithinBBox } from 'src/common-services/utils/map-tiles'
import { amDateShema } from 'src/common-services/types/alphamap/generic-types'
import { amMobilityRankScopeSchema } from 'src/common-services/types/alphamap/mobility-types'
import { amPOIDensitySchema } from 'src/common-services/types/alphamap/auth-types'
import { POI_TILE_ZOOM_LEVEL } from 'src/constants/map-tiles'

const numericString = (schema: ZodTypeAny) =>
  z.preprocess((a) => {
    if (typeof a === 'string') {
      return parseFloat(a)
    } else if (typeof a === 'number') {
      return a
    } else {
      return undefined
    }
  }, schema)

export const amTileRequestBaseShema = z.object({
  z: numericString(z.number()),
  x: numericString(z.number()),
  y: numericString(z.number()),
  density: z.optional(amPOIDensitySchema),
  categories: z.optional(z.string()),
  onlyWithMobility: z.optional(z.enum(['1', '0'])),
})

export type AMTileRequestBase = z.infer<typeof amTileRequestBaseShema>

const validateWithinUSA = (data: AMTileRequestBase) => {
  const { z, x, y } = data
  const boxes = Object.values(VectorSquare)
  for (const box of boxes) {
    if (isWithinBBox(box, { x, y, z })) {
      return true
    }
  }
  return false
}

export const amTileRequestShema = amTileRequestBaseShema.refine(validateWithinUSA, {
  message: 'Tile is not within the USA',
})

export type AMTileRequest = z.infer<typeof amTileRequestShema>

export const amTileLocationsRequestShema = amTileRequestBaseShema
  .extend({
    z: numericString(z.number().min(POI_TILE_ZOOM_LEVEL)),
  })
  .refine(validateWithinUSA, {
    message: 'Tile is not within the USA',
  })

export type AMTileLocationsRequest = z.infer<typeof amTileLocationsRequestShema>

export const amTileLocationsByBrandRequestShema = amTileRequestBaseShema
  .extend({
    brandId: z.string(),
    locationId: z.union([z.string(), z.literal('none')]),
    z: numericString(z.number().min(2)),
    date: z.union([amDateShema, z.literal('none')]),
    scope: amMobilityRankScopeSchema,
  })
  .refine(validateWithinUSA, {
    message: 'Tile is not within the USA',
  })
export type AMTileLocationsByBrandRequest = z.infer<typeof amTileLocationsByBrandRequestShema>

export const amTileLocationsByCategoryRequestShema = amTileRequestBaseShema
  .extend({
    locationId: z.string(),
    z: numericString(z.number().min(2)),
    date: z.union([amDateShema, z.literal('none')]),
    scope: amMobilityRankScopeSchema,
    naics: z.union([z.string(), z.literal('none')]),
  })
  .refine(validateWithinUSA, {
    message: 'Tile is not within the USA',
  })
export type AMTileLocationsByCategoryRequest = z.infer<typeof amTileLocationsByCategoryRequestShema>

export const VISITS_TILE_ZOOM_LEVEL = 7
export const amTileCensusBlockGroupRequestShema = amTileRequestBaseShema
  .extend({
    z: numericString(z.number().min(VISITS_TILE_ZOOM_LEVEL)),
  })
  .refine(validateWithinUSA, {
    message: 'Tile is not within the USA',
  })
export type AMTileCensusBlockGroupRequest = z.infer<typeof amTileCensusBlockGroupRequestShema>

export const amTileZipCodeRequestSchema = amTileRequestBaseShema
  .extend({
    z: numericString(z.number().min(VISITS_TILE_ZOOM_LEVEL)),
  })
  .refine(validateWithinUSA, {
    message: 'Tile is not within the USA',
  })

export type AMTileZipCodeRequest = z.infer<typeof amTileZipCodeRequestSchema>
